import PropTypes from "prop-types";
import { CircleStop, RotateCcw, TriangleAlert } from "lucide-react";
import LoadingSpinner from "../Common/LoadingSpinner";
import { compactNumberSegments } from "../../utils/compactNumbers";

// Every failure used to read "Ask AI could not answer right now", so a
// disabled feature, an unreachable box and a rate limit were
// indistinguishable to both the user and whoever they asked for help —
// even though error_code was already being sent. These say what happened
// and, where it matters, whether retrying is worth anything.
const ERROR_MESSAGES = {
  ai_disabled: "Ask AI is currently turned off. Contact an administrator if you need it.",
  provider_unavailable: "The AI service could not be reached. It may be restarting — try again shortly.",
  provider_timeout: "The AI service took too long to respond. Try again, or ask a narrower question.",
  provider_rate_limited: "The AI service is busy right now. Wait a moment and try again.",
  provider_bad_response: "The AI service returned something unusable. Trying again usually helps.",
  internal_error: "Something went wrong on our side. Try again, and tell an administrator if it keeps happening.",
};
const DEFAULT_ERROR_MESSAGE = "Ask AI could not answer right now. Please try again.";

// Big figures are shortened ("Rp 5.25B") with the exact value on hover.
// Still plain text: each figure is a text node inside a <span>.
function AnswerText({ content }) {
  return compactNumberSegments(content).map((segment, index) =>
    typeof segment === "string" ? (
      segment
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index} title={segment.full}>
        {segment.text}
      </span>
    ),
  );
}

AnswerText.propTypes = {
  content: PropTypes.string.isRequired,
};

// Assistant content is always rendered as plain text — no markdown parser,
// no dangerouslySetInnerHTML — so nothing the model or a prompt-injected
// record could write is ever interpreted as HTML or a clickable URL.
function AskAiMessage({ message, onRetry, onCancel }) {
  const isUser = message.role === "user";
  const isFailed = message.status === "failed";
  const isDegraded = message.status === "degraded";
  const isPending = message.status === "pending";
  const isCancelled = message.status === "cancelled";
  // The task writes the answer to the row as it is generated, so a
  // pending message often already has real text to show.
  const streaming = isPending && !!message.content;
  const errorMessage = ERROR_MESSAGES[message.error_code] || DEFAULT_ERROR_MESSAGE;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-6 ${
            isUser
              ? "bg-accent text-accent-ink"
              : isFailed
                ? "border border-danger/40 bg-danger-surface text-danger"
                : "border border-border bg-surface-raised text-text-secondary"
          }`}
        >
          {isFailed && (
            <span className="flex items-start gap-2">
              <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </span>
          )}
          {isPending && !streaming && (
            <span className="flex items-center gap-2 text-text-muted">
              <LoadingSpinner size="sm" />
              Thinking…
            </span>
          )}
          {streaming && (
            <p className="whitespace-pre-wrap break-words">
              <AnswerText content={message.content} />
              <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-text-muted align-middle" />
            </p>
          )}
          {isCancelled && (
            <span className="text-text-muted italic">Stopped.</span>
          )}
          {!isFailed && !isPending && !isCancelled && (
            <p className="whitespace-pre-wrap break-words">
              {isUser ? message.content : <AnswerText content={message.content} />}
            </p>
          )}
          {isDegraded && (
            <p className="mt-2 text-xs italic text-text-muted">
              AI narration was unavailable — showing the retrieved data directly.
            </p>
          )}
        </div>
        {!isUser && isPending && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-text-muted transition hover:text-danger focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-raised"
          >
            <CircleStop size={13} aria-hidden="true" />
            Stop
          </button>
        )}
        {!isUser && isFailed && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-text-muted transition hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-raised"
          >
            <RotateCcw size={13} aria-hidden="true" />
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

AskAiMessage.propTypes = {
  message: PropTypes.shape({
    message_id: PropTypes.string,
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
    status: PropTypes.string,
    error_code: PropTypes.string,
  }).isRequired,
  onRetry: PropTypes.func,
  onCancel: PropTypes.func,
};

AskAiMessage.defaultProps = {
  onRetry: null,
  onCancel: null,
};

export default AskAiMessage;
